/**
 * DropdownListsPage — "القوائم المنسدلة" hub under ASB606 ADMIN (Control).
 * Each card manages one dropdown list used across the admin panel.
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { List, UserCog } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { usePerms } from '../permissions/PermissionsContext';

export default function DropdownListsPage() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const CARDS = [
    {
      id: 'job-titles',
      icon: UserCog,
      color: '#d4af37',
      titleAr: 'المسميات الوظيفية',
      titleEn: 'Job Titles',
      descAr: 'إدارة قائمة المسميات الوظيفية',
      descEn: 'Manage job titles dropdown',
      route: '/control-panel-settings/dropdowns/job-titles',
      code: 'DDL-002',
    },
    // iter-218m (Feb 2026) — "أنواع الجهات" moved to Finance ▸ Dropdown Lists
    // (/dashboard/finance/settings/dropdown-lists) because it's a finance-only
    // list. The old route /control-panel-settings/dropdowns/entity-types is
    // still registered for direct-link back-compat.
  ];

  return (
    <DashboardLayout
      title="القوائم المنسدلة"
      titleEn="Dropdown Lists"
      subtitle="إدارة القوائم المنسدلة المستخدمة في لوحة التحكم"
      subtitleEn="Manage dropdown lists used across the admin panel"
      screenCode="ASB-DDL"
      backPath="/control-panel-settings"
      onBack={() => navigate('/control-panel-settings')}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'القوائم المنسدلة', labelEn: 'Dropdown Lists' },
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dropdown-lists-page">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {visiblePages(CARDS, 'route').map((card) => (
            <motion.div
              key={card.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              whileHover={{ y: -5 }}
              onClick={() => navigate(card.route)}
              className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer"
              data-testid={`dropdown-card-${card.id}`}
            >
              <div className="absolute top-3 left-3 px-2 py-0.5 bg-white/5 rounded-md">
                <span className="text-[10px] font-mono text-[#71717A]">{card.code}</span>
              </div>
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                style={{ backgroundColor: `${card.color}15`, border: `1px solid ${card.color}30` }}
              >
                <card.icon className="h-8 w-8" style={{ color: card.color }} />
              </div>
              <h3 className="text-base font-bold text-white text-center mb-1">
                {isAr ? card.titleAr : card.titleEn}
              </h3>
              <p className="text-xs text-[#71717A] text-center">
                {isAr ? card.descAr : card.descEn}
              </p>
            </motion.div>
          ))}

          {/* Placeholder for future lists */}
          <div className="bg-white/[0.01] rounded-2xl p-6 border border-dashed border-white/10 flex flex-col items-center justify-center text-center">
            <List className="h-8 w-8 text-white/20 mb-3" />
            <p className="text-xs text-[#71717A]">
              {isAr ? 'قوائم إضافية قريباً' : 'More lists coming soon'}
            </p>
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}
